import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import FFLoader from '../components/FFLoader';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const verifyCalledRef = useRef(false);

  const token = searchParams.get('token');

  const fireConfetti = useCallback(() => {
    const end = Date.now() + 1500;
    const colors = ['#FF6B6B', '#22c55e', '#3b82f6', '#f59e0b', '#a855f7'];
    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  useEffect(() => {
    if (verifyCalledRef.current) return;
    if (token) {
      verifyCalledRef.current = true;
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link');
      setErrorDetails('No verification token found in the URL.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await authApi.verifyEmail(token);
      
      setStatus('success');
      setMessage(response.message || 'Email verified successfully!');
      fireConfetti();
      
      // Redirect to login after 3 seconds (let confetti play)
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage('Verification failed');
      setErrorDetails(error.response?.data?.detail || 'The verification link may be invalid or expired.');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center py-12 px-4" data-testid="verify-email-page">
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-[#FF6B6B] mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Verifying your email...</h1>
            <p className="text-zinc-500">Please wait while we confirm your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold mb-4">{message}</h1>
            <p className="text-zinc-500 mb-6">Redirecting to login...</p>
            <FFLoader size={32} />
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-4">{message}</h1>
            <p className="text-zinc-500 mb-6">{errorDetails}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full">
                  Go to Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="outline" className="w-full">
                  <Mail className="w-4 h-4 mr-2" />
                  Sign Up Again
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
